import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { dnAlert } from "./utils/dNews_Alert";
import { FaEdit, FaTrash, FaExternalLinkAlt, FaBell, FaSearch } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import { apiFetch } from "./utils/api";
import "./CreateNotification.css";

// react-select renders its menu inline by default, so any ancestor with
// overflow:hidden (our form cards) clips it. Portal the menu to <body>.
const SELECT_PORTAL = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

// NOTE: there is deliberately NO "general / public" option here, unlike the
// Banner, Updates and Policies pages. Notifications are delivered through the
// portal's bell, and the bell only renders for logged-in employees - a public
// notification would have nowhere to appear. "All Employees" is the widest
// audience a notification can actually reach. Don't add "general" back without
// first giving visitors a bell.
const AUDIENCE_TYPES = [
    { value: "all_employees", label: "All Employees (logged-in staff)" },
    { value: "department", label: "Department" },
    { value: "job_position", label: "Job Position" },
    { value: "location", label: "Location" },
    { value: "access_level", label: "Access Level" },
    { value: "employee", label: "Employee" },
];
const SCOPED = ["department", "job_position", "location", "access_level", "employee"];
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];
// Only the emoji is stored (notifications.icon, varchar(16)); the label is a
// tooltip so the intent of each icon is obvious at a glance.
const ICONS = [
    { e: "📢", label: "Announcement" },
    { e: "🎉", label: "Celebration" },
    { e: "⚠️", label: "Warning" },
    { e: "📌", label: "Pinned / important" },
    { e: "✅", label: "Completed" },
    { e: "🗓️", label: "Schedule / date" },
    { e: "💡", label: "Tip / idea" },
    { e: "ℹ️", label: "Information" },
    { e: "🔔", label: "Reminder" },
    { e: "❌", label: "Cancelled" },
    { e: "🚨", label: "Emergency" },
    { e: "⏰", label: "Alarm / deadline" },
    { e: "⚡", label: "Power outage" },
    { e: "🛠️", label: "Maintenance" },
    { e: "🔒", label: "Security / access" },
    { e: "🌐", label: "Network / internet" },
];

const isHttpUrl = (u) => { try { const x = new URL(u); return x.protocol === "http:" || x.protocol === "https:"; } catch { return false; } };
const truncate = (t = "", n = 120) => (t && t.length > n ? t.slice(0, n) + "…" : t);

// Repeat = "once" is the classic one-off notification. Anything else is a RULE
// the scheduler fires on a cadence, saved to a separate table.
const REPEATS = [
    { value: "once", label: "Once" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "custom", label: "Every N days" },
];
const WEEKDAYS = [
    { v: 0, l: "Sun" }, { v: 1, l: "Mon" }, { v: 2, l: "Tue" }, { v: 3, l: "Wed" },
    { v: 4, l: "Thu" }, { v: 5, l: "Fri" }, { v: 6, l: "Sat" },
];

const EMPTY = {
    editingId: null, editingRuleId: null,
    audience_type: "all_employees", audience_value: [], icon: "", title: "", message: "", link_url: "", priority: "normal",
    display_from: "", display_to: "",
    repeat: "once",
    days_of_week: [], day_of_month: 1, interval_days: 2, time_of_day: "10:00",
};

const CreateNotification = ({ navSize }) => {
    const [form, setForm] = useState({ ...EMPTY });
    const [list, setList] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [rules, setRules] = useState([]);
    const formRef = useRef(null);

    useEffect(() => { fetchList(); fetchRules(); fetchDepartments(); fetchJobPositions(); fetchEmployees(); }, []);

    const fetchList = async () => { try { const r = await apiFetch("/api/Dolluzcorp/notifications/manual"); setList(await r.json()); } catch (e) { console.error(e); } };
    const fetchRules = async () => { try { const r = await apiFetch("/api/Dolluzcorp/notification-rules"); const d = await r.json(); if (d.success) setRules(d.data); } catch (e) { console.error(e); } };
    const fetchDepartments = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-department_config"); setDepartments(await r.json()); } catch { /* ignore */ } };
    const fetchJobPositions = async () => { try { const r = await apiFetch("/api/Employee_configuration/get-job_position_config"); setJobPositions(await r.json()); } catch { /* ignore */ } };
    const fetchEmployees = async () => { try { const r = await apiFetch("/api/employee/directory"); setEmployees(await r.json()); } catch { /* ignore */ } };

    const patch = (p) => setForm(prev => ({ ...prev, ...p }));
    const resetForm = () => setForm({ ...EMPTY });

    const audienceOptionsFor = (type) => {
        switch (type) {
            case "department": return departments.map(d => ({ value: String(d.department_id), label: d.department_name }));
            case "job_position": return jobPositions.map(j => ({ value: String(j.job_id), label: j.job_name }));
            case "location": return LOCATIONS.map(l => ({ value: l, label: l }));
            case "access_level": return ACCESS_LEVELS.map(l => ({ value: l, label: l }));
            case "employee": return employees.map(e => ({ value: String(e.emp_id), label: `${e.emp_first_name} ${e.emp_last_name}` }));
            default: return [];
        }
    };

    const audienceBadge = (n) => {
        const t = n.notification_category;
        // Legacy rows created before "general" was removed from this page. The
        // bell is employee-only, so they only ever reached employees anyway.
        if (t === "general" || t === "all_employees") return "All Employees";
        const count = (n.notification_for && n.notification_for !== "ALL") ? n.notification_for.split(",").filter(Boolean).length : 0;
        const label = { department: "Department", job_position: "Job Position", location: "Location", access_level: "Access Level", employee: "Employee" }[t] || t;
        return `${label}${count ? ` (${count})` : ""}`;
    };

    const editItem = (n) => {
        setForm({
            ...EMPTY,
            editingId: n.notification_id,
            // Map legacy "general" onto "all_employees" so the Select has a
            // matching option instead of rendering blank.
            audience_type: (!n.notification_category || n.notification_category === "general")
                ? "all_employees"
                : n.notification_category,
            audience_value: (n.notification_for && n.notification_for !== "ALL") ? n.notification_for.split(",").filter(Boolean) : [],
            icon: n.icon || "",
            title: n.title || "",
            message: n.notification_value || "",
            link_url: n.link_url || "",
            priority: n.priority === "high" ? "high" : "normal",
            display_from: n.display_from || "",
            display_to: n.display_to || "",
            repeat: "once",   // an existing bell notification is always a one-off
        });
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const isRule = form.repeat !== "once";

    const validate = () => {
        if (!form.title.trim()) return "Notification title is required.";
        if (!form.message.trim()) return "Notification message is required.";
        if (SCOPED.includes(form.audience_type) && form.audience_value.length === 0) return "Pick at least one value for the selected audience.";
        if (form.link_url.trim() && !isHttpUrl(form.link_url.trim())) return "Link must start with http:// or https://";
        if (form.repeat === "weekly" && form.days_of_week.length === 0) return "Pick at least one day of the week.";
        if (form.display_from && form.display_to && form.display_from > form.display_to) return "Display From cannot be after Display To.";
        return null;
    };

    const submit = async () => {
        const err = validate();
        if (err) { dnAlert.warning("Check the form", err); return; }

        setSaving(true);
        try {
            const shared = {
                title: form.title.trim(),
                icon: form.icon || null,
                target_type: form.audience_type,
                target_values: SCOPED.includes(form.audience_type) ? form.audience_value : [],
                notification_value: form.message.trim(),
                link_url: form.link_url.trim() || null,
                priority: form.priority,
                display_from: form.display_from || null,
                display_to: form.display_to || null,
            };

            // One-offs live in the notifications table, recurring rules in the
            // rules table. Switching Repeat while editing therefore isn't an
            // update in place - it's a save into the other table plus a cleanup
            // of the old row. wasOnce/wasRule say where the item started.
            const wasOnce = !!form.editingId;
            const wasRule = !!form.editingRuleId;
            const convertToRule = wasOnce && isRule;    // one-off  -> schedule
            const convertToOnce = wasRule && !isRule;   // schedule -> one-off

            let url, body, verb;
            if (isRule) {
                // A recurring rule - saved to the rules table, fired by the scheduler.
                // Update in place only if it was already a rule; a converted
                // one-off has no rule_id yet, so it's a fresh create.
                url = wasRule ? "/api/Dolluzcorp/notification-rules/update" : "/api/Dolluzcorp/notification-rules/create";
                body = {
                    ...shared,
                    frequency: form.repeat,
                    days_of_week: form.days_of_week,
                    day_of_month: form.day_of_month,
                    interval_days: form.interval_days,
                    time_of_day: form.time_of_day,
                    ...(wasRule ? { rule_id: form.editingRuleId } : {}),
                };
                verb = wasRule ? "Schedule updated" : convertToRule ? "Turned into a schedule" : "Schedule created";
            } else {
                // A one-off notification, straight into the bell.
                url = wasOnce ? "/api/Dolluzcorp/notifications/update" : "/api/Dolluzcorp/notifications/create";
                body = wasOnce
                    ? { ...shared, notification_type: "MANUAL", notification_id: form.editingId }
                    : { ...shared, notification_type: "MANUAL" };
                verb = wasOnce ? "Notification updated" : convertToOnce ? "Turned into a one-off" : "Notification published";
            }

            const res = await apiFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
            const data = await res.json();
            if (!data.success) { if (data.devError) console.error("notification save:", data.devError); dnAlert.error("Couldn’t save", data.message || "Something went wrong. Please try again."); return; }

            // The new row is saved - now drop the old one from the other table so
            // the item doesn't end up living in both.
            if (convertToRule) {
                await apiFetch("/api/Dolluzcorp/notifications/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ notification_id: form.editingId }) });
            } else if (convertToOnce) {
                await apiFetch("/api/Dolluzcorp/notification-rules/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rule_id: form.editingRuleId }) });
            }

            dnAlert.success(verb, isRule ? "It will send automatically on schedule." : "Saved successfully.");
            resetForm();
            fetchList();
            fetchRules();
        } catch (e) { dnAlert.error("Server error", e.message); }
        finally { setSaving(false); }
    };

    // ── Recurring rule actions ──
    const editRule = (r) => {
        setForm({
            ...EMPTY,
            editingRuleId: r.rule_id,
            audience_type: r.audience_type || "all_employees",
            audience_value: (r.audience_value && r.audience_value !== "ALL") ? r.audience_value.split(",").filter(Boolean) : [],
            icon: r.icon || "",
            title: r.title || "",
            message: r.message || "",
            link_url: r.link_url || "",
            priority: r.priority === "high" ? "high" : "normal",
            display_from: r.display_from || "",
            display_to: r.display_to || "",
            repeat: r.frequency || "daily",
            days_of_week: (r.days_of_week || "").split(",").filter(s => s !== "").map(Number),
            day_of_month: r.day_of_month || 1,
            interval_days: r.interval_days || 2,
            time_of_day: r.time_of_day || "10:00",
        });
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const toggleRule = async (r) => {
        const res = await apiFetch("/api/Dolluzcorp/notification-rules/toggle", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rule_id: r.rule_id, active: r.active ? 0 : 1 }) });
        const data = await res.json();
        if (!data.success) { dnAlert.error("Error", data.message || "Couldn’t update."); return; }
        fetchRules();
    };

    const deleteRule = async (r) => {
        const ok = await dnAlert.confirm({ title: "Delete this schedule?", message: "It will stop sending. Notifications it already sent stay in the bell.", danger: true, confirmLabel: "Delete" });
        if (!ok) return;
        const res = await apiFetch("/api/Dolluzcorp/notification-rules/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rule_id: r.rule_id }) });
        const data = await res.json();
        if (!data.success) { dnAlert.error("Error", data.message || "Delete failed"); return; }
        dnAlert.success("Deleted", "Schedule removed");
        if (form.editingRuleId === r.rule_id) resetForm();
        fetchRules();
    };

    // Plain-English summary of a rule's cadence for the list.
    const ruleCadence = (r) => {
        const t = r.time_of_day || "10:00";
        if (r.frequency === "daily") return `Every day at ${t}`;
        if (r.frequency === "weekly") {
            const days = (r.days_of_week || "").split(",").filter(s => s !== "").map(Number).sort()
                .map(n => WEEKDAYS[n]?.l).join(", ");
            return `${days || "-"} at ${t}`;
        }
        if (r.frequency === "monthly") return `Day ${r.day_of_month} of each month at ${t}`;
        if (r.frequency === "custom") return `Every ${r.interval_days} days at ${t}`;
        return "";
    };

    const removeItem = async (id) => {
        const ok = await dnAlert.confirm({ title: "Delete notification?", message: "It will be removed from the portal bell for everyone.", danger: true, confirmLabel: "Delete" });
        if (!ok) return;
        const res = await apiFetch("/api/Dolluzcorp/notifications/delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ notification_id: id }) });
        const data = await res.json();
        if (!data.success) { dnAlert.error("Error", data.message || "Delete failed"); return; }
        dnAlert.success("Deleted", "Notification removed");
        if (form.editingId === id) resetForm();
        fetchList();
    };

    const q = search.trim().toLowerCase();
    const filteredList = !q ? list : list.filter(n =>
        (n.title || "").toLowerCase().includes(q) ||
        (n.notification_value || "").toLowerCase().includes(q) ||
        (n.created_by_name || n.created_by || "").toLowerCase().includes(q)
    );

    const isScoped = SCOPED.includes(form.audience_type);
    const todayStr = new Date().toLocaleDateString("en-CA");   // local YYYY-MM-DD, matches Updates
    const audienceLabel = AUDIENCE_TYPES.find(a => a.value === form.audience_type)?.label.split(" - ")[0] || "General";
    const hasDraft = form.title.trim() || form.message.trim() || form.icon;

    return (
        <div className="notification-container">
            <div className={`notification-dashboard ${navSize}`}>
                <div className="ntf-header-wrapper">
                    <h2>
                        <img src={logo_eagle} alt="dAdmin Logo" className="ntf-header-logo" />
                        Notification Creation
                    </h2>
                    <p className="ntf-subtitle">
                        Publish notifications to the Inside D portal's header bell. Compose on the left and watch it drop
                        into the live bell on the right, right above the ones already published.
                    </p>
                </div>

                <div className="notification-dashboard-content">
                    <div className="ntf-grid">
                        {/* ── LEFT: compose ── */}
                        <div className="ntf-compose" ref={formRef}>
                            <div className="ntf-card">
                                <div className="ntf-card-head">
                                    <h4>{form.editingId ? "Edit Notification" : "New Notification"}</h4>
                                    <p>{form.editingId ? "Editing an existing notification." : "Fill the details, then publish to the bell."}</p>
                                </div>

                                <div className="ntf-form">
                                    <label className="ntf-label">Who sees this?<span className="ntf-req"> *</span></label>
                                    <Select
                                        classNamePrefix="ntf-select" {...SELECT_PORTAL}
                                        options={AUDIENCE_TYPES}
                                        value={AUDIENCE_TYPES.find(a => a.value === form.audience_type)}
                                        onChange={(sel) => patch({ audience_type: sel.value, audience_value: [] })}
                                    />
                                    {isScoped && (
                                        <div className="ntf-sub">
                                            <label className="ntf-label">Select {form.audience_type.replace("_", " ")}</label>
                                            <Select
                                                key={form.audience_type}
                                                isMulti
                                                {...checkboxMultiProps}
                                                classNamePrefix="ntf-select" {...SELECT_PORTAL}
                                                options={audienceOptionsFor(form.audience_type)}
                                                value={audienceOptionsFor(form.audience_type).filter(o => form.audience_value.includes(String(o.value)))}
                                                onChange={(sel) => patch({ audience_value: (sel || []).map(s => String(s.value)) })}
                                            />
                                        </div>
                                    )}

                                    <label className="ntf-label">Priority</label>
                                    <div className="ntf-priority">
                                        {[
                                            { v: "normal", l: "Normal", hint: "Listed by date, newest first." },
                                            { v: "high", l: "Priority", hint: "Pinned above every normal notification in the bell." },
                                        ].map(p => (
                                            <button
                                                type="button"
                                                key={p.v}
                                                title={p.hint}
                                                className={`ntf-priority-opt ${form.priority === p.v ? `active ${p.v}` : ""}`}
                                                onClick={() => patch({ priority: p.v })}
                                            >
                                                {p.l}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="ntf-hint ntf-priority-hint">
                                        {form.priority === "high"
                                            ? "Shows above all normal notifications. Two priority ones are ordered newest first."
                                            : "Shown in date order, below any priority notifications."}
                                    </p>

                                    <label className="ntf-label">Icon <span className="ntf-hint">(optional)</span></label>
                                    <div className="ntf-emoji-row">
                                        {ICONS.map(({ e, label }) => (
                                            <button
                                                type="button"
                                                key={e}
                                                title={label}
                                                aria-label={label}
                                                className={`ntf-emoji ${form.icon === e ? "active" : ""}`}
                                                onClick={() => patch({ icon: form.icon === e ? "" : e })}
                                            >{e}</button>
                                        ))}
                                    </div>

                                    <label className="ntf-label">Title<span className="ntf-req"> *</span> <span className="ntf-count-inline">{form.title.length}/120</span></label>
                                    <input className="ntf-input" value={form.title} onChange={(e) => patch({ title: e.target.value })} placeholder="Notification title" maxLength={120} />

                                    <label className="ntf-label">Message<span className="ntf-req"> *</span> <span className="ntf-count-inline">{form.message.length}/300</span></label>
                                    <textarea className="ntf-input ntf-textarea" rows="4" value={form.message} onChange={(e) => patch({ message: e.target.value })} placeholder="Notification message / description" maxLength={300} />

                                    <label className="ntf-label">Link <span className="ntf-hint">(optional - opens when clicked in the bell)</span></label>
                                    <input className="ntf-input" value={form.link_url} onChange={(e) => patch({ link_url: e.target.value })} placeholder="https://…" maxLength={500} />

                                    {/* Repeat: Once = a normal one-off; anything else becomes a
                                        schedule the system sends automatically. Switching this while
                                        editing converts the item between one-off and schedule. */}
                                    <label className="ntf-label">Repeat</label>
                                    <div className="ntf-repeat-seg">
                                        {REPEATS.map(r => (
                                            <button
                                                type="button"
                                                key={r.value}
                                                className={`ntf-repeat-opt ${form.repeat === r.value ? "active" : ""}`}
                                                onClick={() => patch({ repeat: r.value })}
                                            >{r.label}</button>
                                        ))}
                                    </div>

                                    {isRule && (
                                        <div className="ntf-sched">
                                            {form.repeat === "weekly" && (
                                                <div className="ntf-sched-row">
                                                    <label className="ntf-label">On days<span className="ntf-req"> *</span></label>
                                                    <div className="ntf-days">
                                                        {WEEKDAYS.map(d => (
                                                            <button
                                                                type="button"
                                                                key={d.v}
                                                                className={`ntf-day ${form.days_of_week.includes(d.v) ? "active" : ""}`}
                                                                onClick={() => patch({
                                                                    days_of_week: form.days_of_week.includes(d.v)
                                                                        ? form.days_of_week.filter(x => x !== d.v)
                                                                        : [...form.days_of_week, d.v],
                                                                })}
                                                            >{d.l}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {form.repeat === "monthly" && (
                                                <div className="ntf-sched-row">
                                                    <label className="ntf-label">Day of month</label>
                                                    <input type="number" min="1" max="31" className="ntf-input ntf-num"
                                                        value={form.day_of_month}
                                                        onChange={(e) => patch({ day_of_month: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })} />
                                                    <span className="ntf-hint">A day past the month's end fires on its last day.</span>
                                                </div>
                                            )}
                                            {form.repeat === "custom" && (
                                                <div className="ntf-sched-row">
                                                    <label className="ntf-label">Every</label>
                                                    <input type="number" min="1" max="365" className="ntf-input ntf-num"
                                                        value={form.interval_days}
                                                        onChange={(e) => patch({ interval_days: Math.min(365, Math.max(1, Number(e.target.value) || 1)) })} />
                                                    <span className="ntf-hint">days, counted from Display From (or today).</span>
                                                </div>
                                            )}
                                            <div className="ntf-sched-row">
                                                <label className="ntf-label">Time <span className="ntf-hint">(IST)</span></label>
                                                <input type="time" className="ntf-input ntf-time"
                                                    value={form.time_of_day}
                                                    onChange={(e) => patch({ time_of_day: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Common to one-offs and schedules. For a one-off it's the
                                        visible window in the bell; for a schedule it's when the
                                        rule keeps firing. Blank = no limit. */}
                                    <label className="ntf-label">
                                        {isRule ? "Active window" : "Display window"} <span className="ntf-hint">(optional)</span>
                                    </label>
                                    <div className="ntf-dates">
                                        <div>
                                            <span className="ntf-date-lbl">From</span>
                                            <input type="date" className="ntf-input" value={form.display_from} min={todayStr}
                                                onChange={(e) => patch({ display_from: e.target.value, ...(form.display_to && e.target.value > form.display_to ? { display_to: "" } : {}) })} />
                                        </div>
                                        <div>
                                            <span className="ntf-date-lbl">To</span>
                                            <input type="date" className="ntf-input" value={form.display_to} min={form.display_from || todayStr}
                                                onChange={(e) => patch({ display_to: e.target.value })} />
                                        </div>
                                    </div>

                                    <div className="ntf-actions">
                                        {(form.editingId || form.editingRuleId) && <button className="ntf-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>}
                                        <button className="ntf-btn-save" onClick={submit} disabled={saving}>
                                            {saving ? "Saving…"
                                                : isRule
                                                    ? (form.editingRuleId ? "Update schedule" : form.editingId ? "Turn into schedule" : "Create schedule")
                                                    : (form.editingId ? "Update Notification" : form.editingRuleId ? "Turn into one-off" : "Publish Notification")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── RIGHT: live portal bell ── */}
                        <div className="ntf-portal">
                            <div className="ntf-portal-bar">
                                <span className="ntf-portal-brand"><img src={logo_eagle} alt="" /> Inside D</span>
                                <span className="ntf-portal-right">
                                    <span className="ntf-portal-bell">
                                        <FaBell />
                                        {list.length > 0 && <span className="ntf-portal-count">{list.length}</span>}
                                    </span>
                                    <span className="ntf-portal-avatar">A</span>
                                </span>
                            </div>

                            <div className="ntf-bell">
                                <div className="ntf-bell-head">
                                    <span className="ntf-bell-title">Notifications</span>
                                    <div className="ntf-bell-search">
                                        <FaSearch size={11} />
                                        <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
                                    </div>
                                </div>

                                <div className="ntf-bell-list">
                                    {/* live draft */}
                                    {hasDraft && (
                                        <div className="ntf-bell-item draft">
                                            <span className="ntf-bell-tag">{form.editingId ? "Editing" : "Preview"}</span>
                                            <div className="ntf-bell-icon">{form.icon || "🔔"}</div>
                                            <div className="ntf-bell-body">
                                                <div className="ntf-bell-item-title">{form.title.trim() || "Notification title"}</div>
                                                <div className="ntf-bell-item-msg">{form.message.trim() || "Your message will appear here…"}</div>
                                                <div className="ntf-bell-meta">
                                                    {form.priority === "high" && <span className="ntf-badge ntf-badge-high">Priority</span>}
                                                    <span className="ntf-badge ntf-badge-aud">{audienceLabel}</span>
                                                    <span className="ntf-bell-time">just now{form.link_url.trim() ? " · opens a link" : ""}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* published */}
                                    {filteredList.length === 0 && !hasDraft ? (
                                        <div className="ntf-bell-empty">
                                            <div className="ntf-bell-empty-icon">🔔</div>
                                            <p>{list.length === 0 ? "No notifications yet - compose one on the left." : "Nothing matches your search."}</p>
                                        </div>
                                    ) : filteredList.map(n => (
                                        <div className={`ntf-bell-item ${form.editingId === n.notification_id ? "is-editing" : ""}`} key={n.notification_id}>
                                            <div className="ntf-bell-icon">{n.icon || "🔔"}</div>
                                            <div className="ntf-bell-body">
                                                <div className="ntf-bell-item-title">{n.title}</div>
                                                <div className="ntf-bell-item-msg">{truncate(n.notification_value)}</div>
                                                <div className="ntf-bell-meta">
                                                    {n.priority === "high" && <span className="ntf-badge ntf-badge-high">Priority</span>}
                                                    <span className="ntf-badge ntf-badge-aud">{audienceBadge(n)}</span>
                                                    {n.link_url && <a className="ntf-link" href={n.link_url} target="_blank" rel="noopener noreferrer">Link <FaExternalLinkAlt size={8} /></a>}
                                                    <span className="ntf-bell-time">{new Date(n.created_time).toLocaleDateString()} · {n.created_by_name || n.created_by}</span>
                                                </div>
                                            </div>
                                            <div className="ntf-bell-actions">
                                                <button className="ntf-icon-btn" onClick={() => editItem(n)} title="Edit"><FaEdit /></button>
                                                <button className="ntf-icon-btn ntf-icon-del" onClick={() => removeItem(n.notification_id)} title="Delete"><FaTrash /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recurring schedules - rules the system fires automatically. */}
                    <div className="ntf-rules">
                        <div className="ntf-rules-head">
                            <h4>Scheduled notifications <span className="ntf-rules-count">{rules.length}</span></h4>
                            <span className="ntf-hint">Sent automatically. Set “Repeat” above to create one.</span>
                        </div>
                        {rules.length === 0 ? (
                            <div className="ntf-rules-empty">No schedules yet. Pick a Repeat option above to add one.</div>
                        ) : (
                            <div className="ntf-rules-grid">
                                {rules.map(r => (
                                    <div key={r.rule_id} className={`ntf-rule-card ${r.active ? "" : "paused"} ${form.editingRuleId === r.rule_id ? "editing" : ""}`}>
                                        <div className="ntf-rule-top">
                                            <span className="ntf-rule-icon">{r.icon || "🔔"}</span>
                                            <span className="ntf-rule-title">{r.title}</span>
                                            <span className={`ntf-rule-status ${r.active ? "on" : "off"}`}>{r.active ? "Active" : "Paused"}</span>
                                        </div>
                                        <div className="ntf-rule-cadence">{ruleCadence(r)}</div>
                                        <div className="ntf-rule-meta">
                                            <span className="ntf-badge ntf-badge-aud">{audienceBadge({ notification_category: r.audience_type, notification_for: r.audience_value })}</span>
                                            {r.priority === "high" && <span className="ntf-badge ntf-badge-high">Priority</span>}
                                            {(r.display_from || r.display_to) && (
                                                <span className="ntf-badge ntf-badge-win">{r.display_from || "…"} → {r.display_to || "…"}</span>
                                            )}
                                            <span className="ntf-rule-fired">{r.times_fired || 0} sent</span>
                                        </div>
                                        <div className="ntf-rule-actions">
                                            <button className="ntf-rule-btn" onClick={() => toggleRule(r)}>{r.active ? "Pause" : "Resume"}</button>
                                            <button className="ntf-rule-btn" title="Edit" onClick={() => editRule(r)}><FaEdit /></button>
                                            <button className="ntf-rule-btn danger" title="Delete" onClick={() => deleteRule(r)}><FaTrash /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateNotification;
